import { SparklesIcon } from "lucide-react";
import { useEffect, useImperativeHandle, useMemo, useState, type Ref } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { useFuenteDeLaDescripcion } from "@/hooks/useFuenteDeLaDescripcion";
import { useRellenarPlantacionesConIa, useResolverElRelleno } from "@/hooks/useRellenoConIa";
import { getApiErrorMessage } from "@/lib/api";
import type {
  CamposDeLaFila,
  GuardarLaPestanaInput,
  Informe,
  PlantacionDeInforme,
} from "@/lib/informes";
import { formatearCifra } from "@/lib/montos";
import { cn } from "@/lib/utils";

import type { PlantacionPropuesta } from "@/lib/rellenoConIa";

import { BarraDeGuardado } from "./BarraDeGuardado";
import { DescripcionDeLasPlantaciones } from "./DescripcionDeLasPlantaciones";
import { DialogoDePlantacion, type GuardadoDeLaPlantacion } from "./DialogoDePlantacion";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { FilasPropuestas } from "./FilasPropuestas";
import { FuenteDeLaPropuesta } from "./FuenteDeLaPropuesta";
import { ListaDePlantaciones } from "./ListaDePlantaciones";
import { NotasDeLaPestana } from "./NotasDeLaPestana";
import {
  AvisoDeLoRellenado,
  BotonDeRellenoConIa,
  type AvisoDelRelleno,
} from "./RellenoConIa";
import {
  formularioDesdeLaPlantacionPropuesta,
  resumenDeLoPropuestoEnPlantaciones,
  yaEstaEnElCuadroDePlantaciones,
} from "./plantacionesConIa";
import { camposDesdePlantacion } from "./plantaciones";
import {
  campoDeLaDescripcionDePlantaciones,
  itemsPorEnlazarDePlantaciones,
  seccionDePlantaciones,
  tituloDelAviso,
  type TerrenoDeLaLista,
} from "./plantacionesTerreno";
import { modoDelTerreno } from "./cuadroDeConstrucciones";
import { VisorDeFotos } from "./FotosDeTerreno";
import { TerrenoDeLaDescripcion } from "./TerrenoDeLaDescripcion";
import { fotosDelVisor } from "./registroDeTerreno";
import { superficieEnHectareas } from "./superficies";
import type { EstadoDelTerreno } from "./terrenoDeAntecedentes";

/** Lo que la pantalla puede pedirle a la pestaña desde el aviso de salida. */
export interface PestanaDePlantacionesHandle {
  guardar: () => void;
  marcarGuardado: () => void;
}

interface PestanaDePlantacionesProps {
  informe: Informe;
  /**
   * Lo que el encargado sincronizó desde la App de terreno. Solo se LEE: las
   * plantaciones que creó llegan como filas TERRENO y lo registrado en cada
   * fila se ve bajo ella. Sin él (módulo apagado) la pestaña se dibuja sin
   * ninguna pieza de terreno.
   */
  terreno?: EstadoDelTerreno;
  /** Quién tiene asignada la visita: da el nombre en «aún sin sincronizar». */
  asignado?: string | null;
  /** El "Guardar cambios" de la pestaña está en vuelo. */
  guardando: boolean;
  /** Hay una operación de fila en vuelo (alta, corrección, borrado, foto). */
  ocupada: boolean;
  ref?: Ref<PestanaDePlantacionesHandle>;
  onCambios?: (hayCambios: boolean) => void;
  /** Crea la plantación y, con el id que devuelve, sube sus fotos. */
  onAgregar: (campos: CamposDeLaFila, fotos: File[]) => void;
  onCorregir: (filaId: number, campos: CamposDeLaFila) => void;
  onQuitar: (filaId: number) => void;
  onSubirFoto: (filaId: number, archivo: File) => void;
  onQuitarFoto: (filaId: number, fotoId: number) => void;
  /** Guarda la leyenda libre de una foto de la fila (en el acto, fuera del formulario). */
  onLeyendaDeFoto?: (filaId: number, fotoId: number, leyenda: string | null) => void;
  onGuardar: (input: GuardarLaPestanaInput) => void;
}

/**
 * La pestaña "Descriptor de plantaciones": el cuadro de lo que hay plantado en
 * el predio, agrupado por especie, su descripción general y la casilla que
 * declara que no hay ninguna.
 *
 * ES UN DESCRIPTOR Y NO UNA VALORIZACIÓN, y eso la separa de sus tres
 * hermanas con cuadro: no hay ni un campo en UF, ni vida útil, ni ajuste
 * comercial. Lo que una plantación aporta al predio sale del método de activos
 * biológicos (NIC 41), que todavía no está construido. Por eso su pie suma
 * SUPERFICIE Y PLANTAS y no dinero.
 *
 * EL CUADRO SE AGRUPA POR ESPECIE Y ESO ES SOLO PRESENTACIÓN. El servidor
 * devuelve las filas en el orden en que se cargaron y no sabe nada de grupos;
 * juntar, nombrar el grupo y sumar su pie pasa acá, con `agruparPorEspecie`.
 * La decisión es del stakeholder y fue explícita: "esto solo se despliega de
 * esa forma en el front… sin una tabla nueva en la bd, es solo UX".
 *
 * LA ESPECIE ES EL ÚNICO CAMPO OBLIGATORIO porque es esa clave. Su campo
 * ({@see SelectorDeEspecie}) sugiere las especies ya escritas en el informe
 * mientras se teclea, que es lo que impide que el mismo huerto termine partido
 * en dos grupos por una letra.
 *
 * LA SUPERFICIE VA EN HECTÁREAS Y NO EN LA UNIDAD DEL INFORME, al revés que el
 * cuadro de clases de suelo: su columna vecina es la densidad en plantas POR
 * HECTÁREA, y el producto solo cierra si las dos hablan de lo mismo. La unidad
 * está impresa en el rótulo, en la columna y en los dos pies.
 *
 * DOS GUARDADOS, igual que sus hermanas: cada plantación se escribe en el acto
 * —desde el formulario en línea o desde su modal— y "Guardar cambios" escribe
 * lo que es de la PESTAÑA. POR ESO LAS FILAS NO CUENTAN COMO "CAMBIOS SIN
 * GUARDAR": cuando ese aviso aparece, ya están en el servidor. Lo único de las
 * filas que sí cuenta es lo que la IA propuso: existe solo en el navegador.
 */
export function PestanaDePlantaciones({
  informe,
  terreno,
  asignado = null,
  guardando,
  ocupada,
  ref,
  onCambios,
  onAgregar,
  onCorregir,
  onQuitar,
  onSubirFoto,
  onQuitarFoto,
  onLeyendaDeFoto,
  onGuardar,
}: PestanaDePlantacionesProps) {
  const filas = useMemo(() => informe.plantaciones ?? [], [informe.plantaciones]);
  const estado = informe.secciones?.plantaciones;

  /* --- Lo de terreno --- */

  const registroDeTerreno = terreno?.estado === "ok" ? terreno.registro : undefined;
  const seccionDeTerreno = seccionDePlantaciones(registroDeTerreno);
  const porEnlazar = useMemo(
    () => itemsPorEnlazarDePlantaciones(registroDeTerreno, filas),
    [registroDeTerreno, filas],
  );
  const fotosDelRegistro = useMemo(
    () => (seccionDeTerreno ? fotosDelVisor([seccionDeTerreno], "todo") : []),
    [seccionDeTerreno],
  );
  const [fotoAbierta, setFotoAbierta] = useState<string | null>(null);

  const [noAplica, setNoAplica] = useState(estado?.no_aplica ?? false);
  const [descripcion, setDescripcion] = useState(estado?.descripcion_general ?? "");
  const fuenteDelTexto = useFuenteDeLaDescripcion(descripcion);
  const [validada, setValidada] = useState(estado?.descripcion_general_validada ?? false);
  const [notas, setNotas] = useState(informe.notas?.plantaciones ?? "");

  /* La referencia de "lo guardado" es estado y no las props: después de un
     guardado exitoso la base tiene que moverse a lo que quedó en pantalla, y
     las props recién traen ese dato cuando vuelve el refetch. */
  const [base, setBase] = useState({
    noAplica: estado?.no_aplica ?? false,
    descripcion: estado?.descripcion_general ?? "",
    validada: estado?.descripcion_general_validada ?? false,
    notas: informe.notas?.plantaciones ?? "",
  });

  const [propuestas, setPropuestas] = useState<PlantacionPropuesta[]>([]);

  const [ejecucionPendiente, setEjecucionPendiente] = useState<number | null>(null);

  /*
   * UNA PROPUESTA PENDIENTE ES UN CAMBIO SIN GUARDAR, y una fila ya guardada
   * NO. La distinción es la de siempre —cuando el aviso aparecería, la fila ya
   * está en el servidor— pero lo que la IA propuso existe SOLO en el navegador:
   * cambiar de pestaña lo desmonta (Radix no conserva el contenido inactivo) y
   * se perdía sin que nada lo dijera.
   */
  const cambios =
    Number(noAplica !== base.noAplica) +
    Number(descripcion !== base.descripcion) +
    Number(validada !== base.validada) +
    Number(notas !== base.notas) +
    propuestas.length;
  const hayCambios = cambios > 0;

  useEffect(() => {
    onCambios?.(hayCambios);
  }, [hayCambios, onCambios]);

  useEffect(() => () => onCambios?.(false), [onCambios]);

  useImperativeHandle(ref, () => ({
    guardar,
    marcarGuardado: () => {
      // El panel se vacía porque lo propuesto ya se guardó: dejarlo lo
      // haría ver pendiente sobre filas que el cuadro ya muestra, y el
      // aviso de cambios sin guardar no se apagaría nunca.
      fuenteDelTexto.olvidar();
      setPropuestas([]);
      setAviso(null);

      setBase({ noAplica, descripcion, validada, notas });

      // GUARDAR LA PESTAÑA TAMBIÉN ACEPTA LA PROPUESTA aunque no se haya
      // agregado ninguna plantación: lo que se guardó pudo ser la DESCRIPCIÓN.
      aceptarLaCorrida();
    },
  }));

  /* --- "Rellenar con IA" --- */

  const rellenar = useRellenarPlantacionesConIa(informe.id);
  const resolverElRelleno = useResolverElRelleno();

  const [aviso, setAviso] = useState<AvisoDelRelleno | null>(null);
  const [errorDelRelleno, setErrorDelRelleno] = useState<string | null>(null);


  /** Anota que la propuesta se usó, una sola vez. */
  function aceptarLaCorrida() {
    if (ejecucionPendiente === null) return;

    resolverElRelleno.mutate({ ejecucionId: ejecucionPendiente, resultado: "aceptado" });
    setEjecucionPendiente(null);
  }

  function pedirElRelleno() {
    setErrorDelRelleno(null);
    setAviso(null);

    rellenar.mutate(undefined, {
      onSuccess: ({ ejecucion_id, propuesta, documentos, pendientes, sin_usar }) => {
        setPropuestas(propuesta.plantaciones);
        setEjecucionPendiente(ejecucion_id);

        // LA DESCRIPCIÓN SOLO SE ESCRIBE SI ESTÁ VACÍA, misma regla que en las
        // otras pestañas: un texto no se agrega, se pisa — y este lleva casilla
        // "Descripción validada". Se lee del ESTADO ACTUAL y no de una copia,
        // porque leer el expediente tarda y quien apretó sigue escribiendo.
        const propuso = propuesta.descripcion_general?.trim() ?? "";
        let escribio = false;

        // LA FUENTE DEL TEXTO SE MUESTRA SOLO MIENTRAS EL TEXTO SIGA SIENDO EL
        // PROPUESTO (el hook lo compara): si el campo ya tenía algo, la IA no
        // escribió y no hay fuente que mostrar. Un relleno nuevo reemplaza la
        // anterior.
        fuenteDelTexto.olvidar();
        if (propuso !== "") {
          fuenteDelTexto.registrar(propuso, propuesta.descripcion_general_fuente ?? null);
        }

        if (propuso !== "") {
          setDescripcion((actual) => {
            if (actual.trim() !== "") return actual;

            escribio = true;

            return propuso;
          });
        }

        setAviso({
          resumen: resumenDeLoPropuestoEnPlantaciones(
            propuesta.plantaciones.length,
            escribio,
          ),
          documentos,
          pendientes,
          sinUsar: sin_usar,
        });
      },

      onError: (e) =>
        setErrorDelRelleno(
          getApiErrorMessage(
            e,
            "No pudimos leer el expediente. Inténtalo de nuevo o carga las plantaciones a mano.",
          ),
        ),
    });
  }


  /** Descarta lo que queda de la propuesta. Lo ya agregado no se toca. */
  function descartarElRelleno() {
    if (ejecucionPendiente !== null) {
      resolverElRelleno.mutate({ ejecucionId: ejecucionPendiente, resultado: "descartado" });
      setEjecucionPendiente(null);
    }

    fuenteDelTexto.olvidar();
    setPropuestas([]);
    setAviso(null);
  }

  /* --- El modal de edición --- */

  /** Qué fila está abierta en el modal, o `null` si está cerrado. */
  const [abierta, setAbierta] = useState<number | null>(null);
  const enEdicion = abierta === null ? null : (filas.find((f) => f.id === abierta) ?? null);

  function guardarDesdeElModal(fila: PlantacionDeInforme, guardado: GuardadoDeLaPlantacion) {
    // LA CASILLA VIAJA CON LOS CAMPOS: si la fila estaba validada y se tocó un
    // dato, el modal ya decidió que vuelve a quedar sin validar.
    onCorregir(fila.id, { ...camposDesdePlantacion(guardado.formulario), validada: guardado.validada });
    setAbierta(null);
  }

  function guardar() {
    // LO PROPUESTO SE GUARDA ACÁ, no fila por fila. El porqué está en
    // `FilasPropuestas`: quien mira la pantalla va al botón grande, no a
    // los chicos de cada fila. Las que YA están no se repiten.
    propuestas
      .filter((plantacion) => !yaEstaEnElCuadroDePlantaciones(plantacion, filas))
      .forEach((plantacion) =>
        onAgregar(camposDesdePlantacion(formularioDesdeLaPlantacionPropuesta(plantacion)), []),
      );

    onGuardar({
      no_aplica: noAplica,
      // Vaciar el textarea BORRA la descripción: `""` viaja como `null`, que
      // es lo que la columna guarda cuando no hay prosa.
      descripcion_general: descripcion.trim() === "" ? null : descripcion,
      descripcion_general_validada: validada,
      notas_observaciones: notas.trim() === "" ? null : notas,
    });
  }

  /** Vuelve la pestaña a lo último guardado y suelta lo que la IA propuso. */
  function descartarLosCambios() {
    setNoAplica(base.noAplica);
    setDescripcion(base.descripcion);
    setValidada(base.validada);
    setNotas(base.notas);
    descartarElRelleno();
  }

  const hayFilas = filas.length > 0;

  /** Todo lo que la lista necesita del terreno, o `undefined` con el módulo apagado. */
  const terrenoDeLaLista: TerrenoDeLaLista | undefined = registroDeTerreno
    ? {
        informeId: informe.id,
        modo: modoDelTerreno(terreno),
        encargado: registroDeTerreno.encargado?.nombre ?? null,
        items: seccionDeTerreno?.items ?? [],
        porEnlazar,
        onAbrirFoto: setFotoAbierta,
        // "Es la misma que": un PATCH de la fila elegida. El hook relee el
        // registro y la plantación de terreno deja de listarse suelta.
        onVincular: (uuid, plantacionId) => onCorregir(plantacionId, { terreno_ref: uuid }),
      }
    : undefined;

  return (
    <div className="flex flex-col gap-3.5">
      {noAplica ? null : (
        <section className="border-success-dot/25 bg-success-soft/50 flex flex-wrap items-center gap-3.5 rounded-xl border-[1.5px] px-4 py-3">
          <span className="bg-success-soft text-success-soft-foreground flex size-8.5 flex-none items-center justify-center rounded-lg">
            <SparklesIcon className="size-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-ink text-[0.8125rem] font-bold">
              Cargar plantaciones desde los documentos
            </h3>
            <p className="text-ink-muted mt-0.5 text-[0.71875rem] font-semibold text-pretty">
              La IA lee los documentos de Antecedentes legales y de Antecedentes históricos y
              propone los cuarteles que encuentre (por ejemplo, del catastro de plantaciones).
              <br />
              Nada se guarda hasta que revises lo propuesto y aprietes «Guardar cambios».
            </p>
          </div>
          <div className="flex-none">
            <BotonDeRellenoConIa
              rellenando={rellenar.isPending}
              // Con la casilla puesta no hay nada que rellenar: proponerle diez
              // cuarteles a quien acaba de declarar que no hay ninguno sería
              // contradecirlo.
              disabled={guardando || ocupada || noAplica}
              variante="oscura"
              onRellenar={pedirElRelleno}
            />
          </div>
        </section>
      )}

      <AvisoDeLoRellenado
        rellenando={rellenar.isPending}
        error={errorDelRelleno}
        aviso={aviso}
        onDescartar={propuestas.length > 0 ? descartarElRelleno : null}
        // LO PROPUESTO SE GUARDA CON LA PESTAÑA, no fila por fila, así que el
        // aviso tiene que mandar al botón de abajo. Decía "cada una se guarda
        // al agregarla" y eso describía el modelo anterior: mandaba a apretar
        // un botón que ya no existe, y contradecía al panel que está justo
        // debajo. Es el mismo defecto que originó el cambio — el texto
        // llevando a la persona al lugar equivocado.
        queHacer={
          "Nada se guardó todavía. Revisa lo propuesto abajo y quita lo que no "
          + "corresponda; las plantaciones que queden se guardan al apretar «Guardar "
          + "cambios», al pie de la pestaña."
        }
      />

      {noAplica ? null : (
        /* LAS COLUMNAS SE DECLARAN ACÁ: son lo único propio de esta pestaña. El
           panel aporta las decisiones —no esconder lo repetido, avisar que
           agregar guarda— que comparten las pestañas con cuadro.

           EL PANEL NO AGRUPA POR ESPECIE, al revés que el cuadro de abajo. La
           agrupación existe para leer un inventario ya cargado; una propuesta
           se revisa fila por fila para decidir cuál entra, y agruparla
           escondería el orden en que el documento las trae. */
        <FilasPropuestas
          filas={propuestas}
          titulo={(cantidad) =>
            cantidad === 1
              ? "1 plantación propuesta por la IA"
              : `${cantidad} plantaciones propuestas por la IA`
          }
          yaEsta={(p) => yaEstaEnElCuadroDePlantaciones(p, filas)}
          fuente={(fila) => fila.fuente}
          ocupada={ocupada}
          onQuitar={(p) => setPropuestas((actuales) => actuales.filter((otra) => otra !== p))}
          columnas={[
            { encabezado: "Especie", celda: (p) => p.especie },
            { encabezado: "Variedad", celda: (p) => p.variedad ?? "—" },
            { encabezado: "Cuartel", celda: (p) => p.cuartel ?? "—" },
            { encabezado: "Año", celda: (p) => p.anio_plantacion ?? "—" },
            {
              encabezado: "Superficie há",
              // `String()` porque los formateadores de `lib/montos` reciben el
              // string de un `decimal:*` de Eloquent, y una PROPUESTA trae
              // números: no hay columna que castear, es lo que respondió el
              // modelo.
              celda: (p) =>
                formatearCifra(p.superficie_ha === null ? null : String(p.superficie_ha)),
              numerica: true,
            },
            {
              // "pl/há" y no "Plantas" a secas: es la DENSIDAD y no el total
              // del cuartel, que es justo la confusión que este cuadro
              // provoca en el documento de origen.
              encabezado: "Densidad pl/há",
              celda: (p) =>
                formatearCifra(p.plantas_por_ha === null ? null : String(p.plantas_por_ha)),
              numerica: true,
            },
            { encabezado: "Distancia", celda: (p) => p.distancia_plantacion ?? "—" },
            { encabezado: "Riego", celda: (p) => p.sistema_riego ?? "—" },
          ]}
        />
      )}

      <section className="bg-card flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-4 py-3">
        <label
          title={hayFilas ? "Quita primero las plantaciones de la lista" : undefined}
          className={cn(
            "flex w-fit items-center gap-2.5",
            hayFilas && !noAplica ? "cursor-not-allowed opacity-55" : "cursor-pointer",
          )}
        >
          <Checkbox
            checked={noAplica}
            // Con filas cargadas el registro se contradiría solo, y el servidor
            // lo rechaza con un 422. Se apaga acá para que el rechazo no sea la
            // forma de enterarse.
            disabled={guardando || hayFilas}
            onCheckedChange={(v) => setNoAplica(v === true)}
          />
          <span className="text-[0.8125rem] font-bold">La propiedad no cuenta con plantaciones</span>
        </label>
        {noAplica || hayFilas ? (
          <span className="text-ink-muted text-[0.71875rem] font-semibold">
            {noAplica
              ? "La sección se imprimirá indicando que no hay plantaciones."
              : "Para marcarlo, quita antes las plantaciones de la lista."}
          </span>
        ) : null}
      </section>

      {porEnlazar.length > 0 && !noAplica ? (
        <section
          aria-label="Plantaciones creadas en terreno"
          className="bg-info-soft/50 border-info-dot/30 flex flex-wrap items-center gap-3 rounded-xl border-[1.5px] px-4 py-3"
        >
          <span aria-hidden="true" className="bg-info-dot size-2 flex-none rounded-full" />
          <div className="min-w-0 flex-1">
            <h3 className="text-ink text-[0.8125rem] font-bold">{tituloDelAviso(porEnlazar.length)}</h3>
            <p className="text-ink-body mt-0.5 text-[0.71875rem] font-semibold text-pretty">
              Llegan desde la App con lo que el encargado anotó. Completa lo que falte a partir de
              sus fotos y audios, o indica si es la misma que una ya cargada desde documentos.
            </p>
          </div>
        </section>
      ) : null}

      {/* Con la casilla puesta no se dibuja la lista: la pestaña ya dijo lo
          único que tenía que decir. */}
      {noAplica ? null : (
        <ListaDePlantaciones
          filas={filas}
          ocupada={ocupada}
          onAgregar={onAgregar}
          onAbrir={(fila) => setAbierta(fila.id)}
          // SE GUARDA SOLA y viaja sin el resto de los campos, para no pisar
          // lo que otro pudo haber corregido mientras tanto.
          onValidar={(fila, validadaAhora) => onCorregir(fila.id, { validada: validadaAhora })}
          terreno={terrenoDeLaLista}
          // CONVERTIDA ACÁ: la unidad es del informe y la lista suma en hectáreas.
          superficieDelPredioHa={superficieEnHectareas(informe.superficie_ha, informe.unidad_superficie)}
        />
      )}

      <DescripcionDeLasPlantaciones
        informeId={informe.id}
        numero={noAplica ? "01" : "02"}
        descripcion={descripcion}
        onDescripcion={setDescripcion}
        validada={validada}
        onValidada={setValidada}
        disabled={guardando}
        fuente={
          fuenteDelTexto.visible ? <FuenteDeLaPropuesta fuente={fuenteDelTexto.visible.fuente} /> : null
        }
        terreno={
          <TerrenoDeLaDescripcion
            informeId={informe.id}
            terreno={terreno}
            campo={campoDeLaDescripcionDePlantaciones(registroDeTerreno)}
            campoRedactable="plantaciones"
            valorActual={descripcion}
            asignado={asignado}
            onAbrirFoto={setFotoAbierta}
            onRedactado={(texto) => {
              // Lo escribió un modelo: nadie lo revisó todavía, y la fuente que
              // declaró el relleno anterior ya no es la de este texto.
              fuenteDelTexto.olvidar();
              setDescripcion(texto);
              setValidada(false);
            }}
          />
        }
      />

      <section className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
        <EncabezadoNumerado
          numero={noAplica ? "02" : "03"}
          idDelTitulo="plantaciones-notas-titulo"
          titulo="Notas u observaciones"
          bajada="Opcional."
        />
        <div className="mt-3.5">
          <NotasDeLaPestana
            id="notas-plantaciones"
            value={notas}
            onChange={setNotas}
            disabled={guardando}
            plana
          />
        </div>
      </section>

      {/* LA BARRA VA AL PIE de la pestaña. Cuenta lo que es de la PESTAÑA
          (casilla, descripción, validación, notas y cada propuesta de la IA);
          las filas no entran porque ya están guardadas. */}
      <BarraDeGuardado
        cambios={cambios}
        enviando={guardando}
        etiquetaEnvio="Guardar cambios"
        ayuda="Las plantaciones y sus fotos se guardan solas al agregarlas o editarlas."
        onGuardar={guardar}
        onDescartar={descartarLosCambios}
      />

      <DialogoDePlantacion
        fila={enEdicion}
        filas={filas}
        informeId={informe.id}
        ocupada={ocupada}
        onGuardar={guardarDesdeElModal}
        onQuitar={(fila) => {
          onQuitar(fila.id);
          setAbierta(null);
        }}
        onCerrar={() => setAbierta(null)}
        onSubirFoto={onSubirFoto}
        onQuitarFoto={onQuitarFoto}
        onLeyendaDeFoto={onLeyendaDeFoto}
      />

      {registroDeTerreno ? (
        <VisorDeFotos
          informeId={informe.id}
          fotos={fotosDelRegistro}
          abierta={fotoAbierta}
          onCambiar={setFotoAbierta}
        />
      ) : null}
    </div>
  );
}
